/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: Combobox.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Combobox component for searchable dropdown selections with
    filtering, keyboard navigation, and accessibility features.

 🔧 Notes:
    - Implements searchable dropdown with filtering capabilities
    - Supports keyboard navigation and accessibility features
    - Integrates with Tailwind CSS for consistent styling
    - Provides smooth animations and transitions
    - Supports custom className and onChange handlers

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';

interface ComboboxOption {
  value: string;
  label: string;
  checked: boolean;
}

interface ComboboxProps {
  options: ComboboxOption[];
  onToggle: (value: string) => void;
  placeholder?: string;
  className?: string;
  single?: boolean;
  translations?: {
    allSelected?: string;
    selected?: string;
    searchOptions?: string;
    noOptionsFound?: string;
    selectAll?: string;
    clearAll?: string;
  };
}

function Combobox({
  options,
  onToggle,
  placeholder = "Select options...",
  className = "",
  translations,
  single = false
}: ComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query (memoized)
  const filteredOptions = useMemo(() =>
    options.filter(option =>
      option.label?.toLowerCase()?.includes(searchQuery.toLowerCase()) ?? false
    ),
    [options, searchQuery]
  );

  // Close combobox when clicking outside (memoized)
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (comboboxRef.current && !comboboxRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSearchQuery('');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Get selected count for display (memoized)
  const selectedCount = useMemo(() =>
    options.filter(option => option.checked).length,
    [options]
  );

  const displayText = useMemo(() => {
    if (single) {
      const selected = options.find(o => o.checked);
      return selected ? selected.label : placeholder;
    }

    if (selectedCount === 0) return placeholder;
    if (selectedCount === options.length) {
      return translations?.allSelected || 'All selected';
    }
    const selectedText = translations?.selected || 'selected';
    return `${selectedCount} ${selectedText}`;
  }, [selectedCount, options, placeholder, translations, single]);

  return (
    <div className={`relative ${className}`} ref={comboboxRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
        title={displayText}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-border">
              <input
                type="text"
                placeholder={translations?.searchOptions || "Search options..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-2 py-1 text-sm bg-background text-foreground placeholder-muted-foreground border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                onClick={(e) => e.stopPropagation()}
                // Auto focus on open
                autoFocus
              />
            </div>

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {translations?.noOptionsFound || "No options found"}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onToggle(option.value);
                      if (single) {
                        setIsOpen(false);
                      }
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors ${option.checked && single ? 'bg-primary/10 text-primary font-medium' : ''
                      }`}
                  >
                    {!single && (
                      <div className={`flex items-center justify-center w-4 h-4 border rounded ${option.checked
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-border'
                        }`}>
                        {option.checked && <Check className="h-3 w-3" />}
                      </div>
                    )}
                    <span className="flex-1 truncate">{option.label}</span>
                    {single && option.checked && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Footer Actions (Only for multiple selection) */}
            {!single && (
              <div className="flex items-center justify-between p-2 border-t border-border bg-muted/30">
                <button
                  type="button"
                  onClick={() => {
                    options.forEach(option => {
                      if (!option.checked) onToggle(option.value);
                    });
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {translations?.selectAll || 'Select All'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    options.forEach(option => {
                      if (option.checked) onToggle(option.value);
                    });
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {translations?.clearAll || 'Clear All'}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default memo(Combobox);
